import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProgramsDropdownOpen, setIsProgramsDropdownOpen] = useState(false);
  const [isAboutDropdownOpen, setIsAboutDropdownOpen] = useState(false);
  const [isSupportDropdownOpen, setIsSupportDropdownOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const navLinks = [
    { 
      name: '회사소개', 
      path: '/about',
      hasDropdown: true,
      subLinks: [
        { name: '회사 비전', path: '/about#vision' },
        { name: '대표자 인사말', path: '/about#ceo' },
        { name: '핵심 가치', path: '/about#core-values' },
      ]
    },
    { 
      name: '사업 프로그램', 
      path: '/programs',
      hasDropdown: true,
      subLinks: [
        { name: '원데이클래스 (현장방문형)', path: '/programs#oneday' },
        { name: '진로콘서트 (학교&기업 방문형)', path: '/programs#career-concert' },
        { name: '국제교류 프로그램', path: '/programs#international' },
        { name: '진로 특강', path: '/programs#career' },
        { name: '대학 탐방', path: '/programs#university' },
        { name: '창업 & 자격증 취득과정', path: '/programs#startup' },
      ]
    },
    { 
      name: '고객지원', 
      path: '/support',
      hasDropdown: true,
      subLinks: [
        { name: '자주 묻는 질문', path: '/support#faq' },
        { name: '프로그램 신청', path: '/apply' },
      ]
    },
  ];

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b border-black/10 ${
          isScrolled || !isHomePage
            ? 'bg-white shadow-sm'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-20 md:h-24">
            {/* Logo */}
            <a
              href="/"
              className="flex items-center gap-2 md:gap-3 group cursor-pointer"
            >
              <div className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center">
                <svg
                  viewBox="0 0 40 54"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  width="100%"
                  height="100%"
                  className="group-hover:scale-110 transition-transform"
                >
                  {/* ── TRUNK ── */}
                  <path
                    d="M20 52 L20 34"
                    stroke="#5C4020"
                    strokeWidth="4.5"
                    strokeLinecap="round"
                  />
                  {/* Root hints */}
                  <path
                    d="M20 47 C17 46 14 47 12 50"
                    stroke="#5C4020"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                  />
                  <path
                    d="M20 47 C23 46 26 47 28 50"
                    stroke="#5C4020"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                  />

                  {/* ── CANOPY — warm brown overlapping circles ── */}
                  <circle cx="20" cy="21" r="13" fill="#8B7355" />
                  <circle cx="20" cy="13" r="9"  fill="#8B7355" />
                  <circle cx="11" cy="23" r="9"  fill="#8B7355" />
                  <circle cx="29" cy="23" r="9"  fill="#8B7355" />
                  <circle cx="14" cy="16" r="8.5" fill="#8B7355" />
                  <circle cx="26" cy="16" r="8.5" fill="#8B7355" />
                  <circle cx="7"  cy="20" r="7"  fill="#8B7355" />
                  <circle cx="33" cy="20" r="7"  fill="#8B7355" />
                  <circle cx="11" cy="12" r="6.5" fill="#8B7355" />
                  <circle cx="29" cy="12" r="6.5" fill="#8B7355" />

                  {/* ── LEAF DEPTH — lighter warm brown overlay ── */}
                  <circle cx="16" cy="18" r="5.5" fill="#C4A882" opacity="0.35" />
                  <circle cx="25" cy="14" r="4.5" fill="#C4A882" opacity="0.28" />
                  <circle cx="13" cy="25" r="4"   fill="#C4A882" opacity="0.25" />
                  <circle cx="28" cy="25" r="4"   fill="#C4A882" opacity="0.25" />
                  <circle cx="20" cy="10" r="4"   fill="#C4A882" opacity="0.3"  />
                  <circle cx="9"  cy="17" r="3.5" fill="#C4A882" opacity="0.22" />
                  <circle cx="31" cy="17" r="3.5" fill="#C4A882" opacity="0.22" />

                  {/* ── LEAF TEXTURE — dark brown accents ── */}
                  <ellipse cx="17" cy="12" rx="3.2" ry="1.5" transform="rotate(-25 17 12)" fill="#6B5640" opacity="0.32" />
                  <ellipse cx="24" cy="10" rx="3"   ry="1.4" transform="rotate(20 24 10)"  fill="#6B5640" opacity="0.28" />
                  <ellipse cx="10" cy="16" rx="2.8" ry="1.3" transform="rotate(-15 10 16)"  fill="#6B5640" opacity="0.26" />
                  <ellipse cx="30" cy="15" rx="2.8" ry="1.3" transform="rotate(15 30 15)"   fill="#6B5640" opacity="0.26" />
                  <ellipse cx="14" cy="23" rx="2.6" ry="1.2" transform="rotate(-10 14 23)"  fill="#6B5640" opacity="0.24" />
                  <ellipse cx="27" cy="22" rx="2.6" ry="1.2" transform="rotate(12 27 22)"   fill="#6B5640" opacity="0.24" />
                  <ellipse cx="20" cy="27" rx="2.8" ry="1.3" transform="rotate(0 20 27)"    fill="#6B5640" opacity="0.22" />

                  {/* ── FRUITS — warm gold ── */}
                  <circle cx="13" cy="21" r="2.4" fill="#D4A820" />
                  <circle cx="26" cy="18" r="2.4" fill="#D4A820" />
                  <circle cx="18" cy="13" r="2.2" fill="#D4A820" />
                  <circle cx="29" cy="24" r="2.2" fill="#D4A820" />
                  <circle cx="20" cy="28" r="2.2" fill="#D4A820" />
                  <circle cx="9"  cy="20" r="2"   fill="#D4A820" />
                  <circle cx="31" cy="18" r="2"   fill="#D4A820" />
                  <circle cx="23" cy="11" r="2"   fill="#D4A820" />
                  <circle cx="15" cy="28" r="2"   fill="#D4A820" />
                  <circle cx="11" cy="14" r="1.8" fill="#E8C040" />
                  <circle cx="28" cy="12" r="1.8" fill="#E8C040" />
                </svg>
              </div>
              <span
                className={`text-xl md:text-2xl font-bold tracking-tight transition-colors ${
                  isScrolled || !isHomePage ? 'text-[#2C2416]' : 'text-[#2C2416]'
                }`}
                style={{ fontFamily: 'Pretendard, sans-serif' }}
              >
                브란치
              </span>
            </a>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8 lg:gap-12">
              {navLinks.map((link) => (
                <div 
                  key={link.path}
                  className="relative group"
                  onMouseEnter={() => {
                    if (link.name === '사업 프로그램') setIsProgramsDropdownOpen(true);
                    if (link.name === '회사소개') setIsAboutDropdownOpen(true);
                    if (link.name === '고객지원') setIsSupportDropdownOpen(true);
                  }}
                  onMouseLeave={() => {
                    if (link.name === '사업 프로그램') setIsProgramsDropdownOpen(false);
                    if (link.name === '회사소개') setIsAboutDropdownOpen(false);
                    if (link.name === '고객지원') setIsSupportDropdownOpen(false);
                  }}
                >
                  <a
                    href={link.path}
                    className={`text-base lg:text-lg font-medium transition-colors cursor-pointer ${
                      isScrolled || !isHomePage
                        ? 'text-[#2C2416] hover:text-[#8B7355]'
                        : 'text-[#2C2416] hover:text-[#8B7355]'
                    }`}
                    style={{ fontFamily: 'Pretendard, sans-serif' }}
                  >
                    {link.name}
                  </a>
                  
                  {/* Dropdown Menu */}
                  {link.hasDropdown && (
                    (link.name === '사업 프로그램' && isProgramsDropdownOpen) ||
                    (link.name === '회사소개' && isAboutDropdownOpen) ||
                    (link.name === '고객지원' && isSupportDropdownOpen)
                  ) && (
                    <div className="absolute top-full left-0 mt-2 w-64 bg-white shadow-lg rounded-lg py-2 border border-gray-100">
                      {link.subLinks?.map((subLink) => (
                        <a
                          key={subLink.path}
                          href={subLink.path}
                          className="block px-4 py-2.5 text-sm text-[#2C2416] hover:bg-[#F5EFE7] hover:text-[#8B7355] transition-colors cursor-pointer"
                          style={{ fontFamily: 'Pretendard, sans-serif' }}
                        >
                          {subLink.name}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <a
                href="/apply"
                className="bg-[#8B7355] text-white px-5 lg:px-6 py-2.5 lg:py-3 rounded-none font-medium hover:bg-[#6F5C44] transition-all whitespace-nowrap cursor-pointer text-sm lg:text-base"
              >
                프로그램 신청
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
              aria-label="메뉴"
            >
              <i
                className={`text-2xl transition-colors ${
                  isScrolled || !isHomePage ? 'text-[#2C2416]' : 'text-[#2C2416]'
                } ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}
              ></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setIsMobileMenuOpen(false)}></div>
          <div className="absolute top-20 left-0 right-0 bg-white shadow-lg overflow-y-auto" style={{ maxHeight: 'calc(100vh - 5rem)' }}>
            <div className="px-4 py-6 space-y-1">
              {navLinks.map((link) => (
                <a
                  key={link.path}
                  href={link.path}
                  className="block px-4 py-3 text-base font-medium text-[#2C2416] hover:bg-[#F5EFE7] transition-colors cursor-pointer rounded-sm"
                  style={{ fontFamily: 'Pretendard, sans-serif' }}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.name}
                </a>
              ))}
              <a
                href="/apply"
                className="block mt-4 bg-[#8B7355] text-white px-4 py-3 text-center rounded-sm font-medium hover:bg-[#6F5C44] transition-all cursor-pointer"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                프로그램 신청
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;